import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';

const MAX_DISCONNECT_SECONDS = 10800;

// The disconnect delay threshold is added to account for delays in identifying
// disconnections (for example, the websocket disconnection takes up to 3 seconds)
// while fetching the latest updated conversations or messages.
const DISCONNECT_DELAY_THRESHOLD = 15;

// Cap the reconnect lookback window. Beyond 30 minutes, conversations.updated_at
// is unreliable as a "recent activity" signal — automation rules, label edits, and
// system bumps move it without customer activity, inflating the list on reconnect.
// Backend enforces the same cap (ConversationFinder::MAX_UPDATED_WITHIN_SECONDS).
const MAX_RECONNECT_LOOKBACK_SECONDS = 1800;

// Watchdog interval and staleness threshold. 60s (rather than the previous
// 120s) bounds the worst-case "my screen is stale" window at ~90s including the
// tick granularity. The cost is one page-1 conversation fetch per agent per
// ~90s on an account with genuinely no activity, which is negligible next to an
// agent sitting in front of a silently frozen inbox.
const WATCHDOG_INTERVAL_MS = 30000;
const REALTIME_STALE_SECONDS = 60;

// The staleness check only fires when the whole account goes quiet. On a busy
// brand the other chats keep the stream fresh, so a message the open chat
// missed (a dropped socket the client never noticed, an event lost between a
// disconnect and the resubscribe) stayed missing until a manual refresh. The
// open chat is re-read on this cadence regardless of account traffic; on a
// healthy stream the request finds nothing and changes nothing.
const OPEN_CHAT_RECONCILE_SECONDS = 60;

// When the tab returns to the foreground after being hidden at least this long,
// force an immediate full resync. Backgrounded tabs have their timers and the
// websocket throttled or frozen by the browser, so the realtime stream is
// unreliable while hidden. Resyncing the instant the agent returns avoids the
// "list is stale / new chats missing until manual refresh" problem instead of
// waiting up to 30s for the watchdog (which is itself disabled while hidden).
const FOREGROUND_RESYNC_HIDDEN_SECONDS = 15;

// Events that ride the websocket but DO NOT indicate conversation activity.
// presence.update fires every ~15s as a heartbeat. If we treat it as a real
// event, the watchdog never sees the websocket as "stale" on low-traffic
// accounts and the auto-resync never fires — leaving brand-new conversations
// invisible until the agent refreshes manually.
// Typing indicators are the same kind of noise: they fire continuously while
// any agent in the account types, carry no conversation or message state, and
// are the single most frequent event on a busy socket (measured 50 of 104
// events in one 50s sample), so counting them hides a stalled stream too.
const NOISY_WEBSOCKET_EVENTS = new Set([
  'presence.update',
  'conversation.typing_on',
  'conversation.typing_off',
]);

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    this.lastWebsocketEventTime = new Date();
    this.watchdogTimer = null;
    this.isWatchdogFetching = false;
    this.hiddenAt = null;
    this.lastResyncAt = null;
    this.lastOpenChatReconcileAt = null;

    this.setupEventListeners();
    this.startWatchdog();
  }

  markResynced = () => {
    this.lastResyncAt = new Date();
  };

  getSecondsSinceLastResync = () =>
    this.lastResyncAt
      ? Math.max(differenceInSeconds(new Date(), this.lastResyncAt), 0)
      : Number.POSITIVE_INFINITY;

  disconnect = () => {
    this.stopWatchdog();
    this.removeEventListeners();
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
    emitter.on(
      BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED,
      this.onWebsocketEventReceived
    );
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
    emitter.off(
      BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED,
      this.onWebsocketEventReceived
    );
  };

  // When the agent returns to the tab after being away, the realtime stream may
  // have been throttled/frozen while backgrounded. Resync immediately (full
  // first-page fetch) so new chats/messages appear without a manual refresh.
  handleVisibilityChange = () => {
    if (typeof document === 'undefined') return;

    if (document.visibilityState === 'hidden') {
      this.hiddenAt = new Date();
      return;
    }
    if (document.visibilityState !== 'visible') return;

    const hiddenFor = this.hiddenAt
      ? Math.max(differenceInSeconds(new Date(), this.hiddenAt), 0)
      : 0;
    this.hiddenAt = null;

    // Quick tab switches keep the live stream, so only resync after a real
    // absence (browsers throttle/suspend background sockets beyond a few secs).
    if (hiddenFor >= FOREGROUND_RESYNC_HIDDEN_SECONDS) {
      this.resyncOnForeground();
    }
  };

  resyncOnForeground = async () => {
    try {
      if (this.isWatchdogFetching) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return;
      // The websocket reconnect flow may fire on the same wake-up and already
      // do this refetch; skip the duplicate full fetch in that case.
      if (this.getSecondsSinceLastResync() < 10) return;

      const currentRoute = this.router.currentRoute.value.name;
      this.isWatchdogFetching = true;
      this.markResynced();
      this.lastWebsocketEventTime = new Date();

      // Full first-page resync so nothing that arrived while hidden is missed
      // (the diff-window lookback can miss messages from a long absence).
      if (isAConversationRoute(currentRoute, true)) {
        await this.fetchConversationsOnReconnect({ fullRefresh: true });
        await this.fetchConversationMessagesOnReconnect();
      } else if (isAInboxViewRoute(currentRoute, true)) {
        await this.fetchNotificationsOnReconnect(
          this.store.getters['notifications/getNotificationFilters']
        );
      } else if (isNotificationRoute(currentRoute)) {
        await this.fetchNotificationsOnReconnect();
      }
      await this.revalidateCaches();
    } catch (e) {
      // noop
    } finally {
      this.isWatchdogFetching = false;
    }
  };

  // Only events for the account currently on screen prove that this view's
  // realtime stream is alive. Agents and admins who belong to several brands
  // receive every brand's traffic on the same socket (measured: 67 of 72 events
  // belonged to other accounts), and those are discarded before they reach the
  // store. Counting them kept lastWebsocketEventTime permanently fresh, so the
  // staleness watchdog below could never fire and a view that had stopped
  // receiving its own events stayed stale until a manual page refresh.
  onWebsocketEventReceived = ({ event, isCurrentAccount = true } = {}) => {
    if (NOISY_WEBSOCKET_EVENTS.has(event)) return;
    if (!isCurrentAccount) return;
    this.lastWebsocketEventTime = new Date();
  };

  getSecondsSinceLastWebsocketEvent = () =>
    this.lastWebsocketEventTime
      ? Math.max(
          differenceInSeconds(new Date(), this.lastWebsocketEventTime),
          0
        )
      : 0;

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  startWatchdog = () => {
    if (this.watchdogTimer) return;
    // Low-rate fallback: if websocket goes stale, resync conversations/messages.
    this.watchdogTimer = setInterval(this.watchdogTick, WATCHDOG_INTERVAL_MS);
  };

  stopWatchdog = () => {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  };

  watchdogTick = async () => {
    try {
      if (this.isWatchdogFetching) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return;

      const currentRoute = this.router.currentRoute.value.name;
      // Only run on conversation-related surfaces to avoid extra load
      if (!isAConversationRoute(currentRoute, true)) return;

      // If we've not seen any conversation-bearing websocket event for a while,
      // assume the realtime stream is silently dropping events.
      // Force a full first-page sync so low-traffic accounts don't miss new chats.
      const staleFor = this.getSecondsSinceLastWebsocketEvent();
      if (staleFor < REALTIME_STALE_SECONDS) {
        await this.reconcileOpenConversation();
        return;
      }

      this.isWatchdogFetching = true;
      this.markResynced();
      this.lastWebsocketEventTime = new Date();

      await this.fetchConversationsOnReconnect({ fullRefresh: true });
      await this.fetchConversationMessagesOnReconnect();
    } catch (e) {
      // noop
    } finally {
      this.isWatchdogFetching = false;
    }
  };

  getSecondsSinceOpenChatReconcile = () =>
    this.lastOpenChatReconcileAt
      ? Math.max(
          differenceInSeconds(new Date(), this.lastOpenChatReconcileAt),
          0
        )
      : Number.POSITIVE_INFINITY;

  reconcileOpenConversation = async () => {
    // A full resync in that window already re-read the open chat.
    if (this.getSecondsSinceLastResync() < OPEN_CHAT_RECONCILE_SECONDS) return;
    if (this.getSecondsSinceOpenChatReconcile() < OPEN_CHAT_RECONCILE_SECONDS)
      return;

    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (!conversationId) return;

    this.lastOpenChatReconcileAt = new Date();
    this.isWatchdogFetching = true;
    await this.store.dispatch('syncActiveConversationMessages', {
      conversationId: Number(conversationId),
      reconcile: true,
    });
  };

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
    }
  };

  // Every fetch dispatched from this service is a background resync (watchdog,
  // websocket reconnect, foreground return), so it passes `silent: true`: the
  // list is refreshed in place without raising listLoadingStatus. Raising it
  // showed the full-pane "Loading Conversations" state on every watchdog tick,
  // which on a quiet account is every 60-90s for the life of the tab.
  fetchConversations = async ({ fullRefresh = false } = {}) => {
    // Watchdog path: low-traffic accounts have no recent updated_at activity,
    // so the diff-window lookback can miss brand-new conversations. Pull page 1
    // in full and skip the lookback filter entirely.
    if (fullRefresh) {
      await this.store.dispatch('updateChatListFilters', {
        page: 1,
        updatedWithin: null,
      });
      await this.store.dispatch('fetchAllConversations', { silent: true });
      return;
    }

    const lookback = Math.min(
      this.getSecondsSinceDisconnect() + DISCONNECT_DELAY_THRESHOLD,
      MAX_RECONNECT_LOOKBACK_SECONDS
    );
    await this.store.dispatch('updateChatListFilters', {
      page: null,
      updatedWithin: lookback,
    });
    await this.store.dispatch('fetchAllConversations', { silent: true });
    // Reset the updatedWithin in the store chat list filter after fetching conversations when the user is reconnected
    await this.store.dispatch('updateChatListFilters', {
      updatedWithin: null,
    });
  };

  fetchFilteredOrSavedConversations = async queryData => {
    await this.store.dispatch('fetchFilteredConversations', {
      queryData,
      page: 1,
      silent: true,
    });
  };

  fetchConversationsOnReconnect = async ({ fullRefresh = false } = {}) => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations({ fullRefresh });
    }
  };

  fetchConversationMessagesOnReconnect = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect();
      await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    } else if (isNotificationRoute(currentRoute)) {
      await this.fetchNotificationsOnReconnect();
    }
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime = new Date();
    this.setConversationLastMessageId();
  };

  onReconnect = async () => {
    this.lastWebsocketEventTime = new Date();
    this.markResynced();
    await this.handleRouteSpecificFetch();
    await this.revalidateCaches();
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
  };
}

export default ReconnectService;
